import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { trainingModules } from './data/modules';
import './TrainingModule.css';

const TrainingModule = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [currentStep, setCurrentStep] = useState('video');
  const [userProgress, setUserProgress] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizScore, setQuizScore] = useState(0);
  const [hasPassed, setHasPassed] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [validUntil, setValidUntil] = useState(null);
  
  // Video engagement tracking
  const [isVideoActive, setIsVideoActive] = useState(true);
  const [userInactiveTime, setUserInactiveTime] = useState(0);
  const [showInactiveWarning, setShowInactiveWarning] = useState(false);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  
  // New state for the popup
  const [showQuizPopup, setShowQuizPopup] = useState(false);
  const [videoCompleted, setVideoCompleted] = useState(false);

  // Calculate 6-month validity date
  const calculateValidityDate = () => {
    const now = new Date();
    // Add 6 months to current date (using 182.5 days for 6 months)
    const validUntilDate = new Date(now.getTime() + (6 * 30.44 * 24 * 60 * 60 * 1000));
    return validUntilDate;
  };

  // Use the imported trainingModules from modules.js
  useEffect(() => {
    console.log('🔄 TrainingModule mounted with moduleId:', moduleId);
    
    const foundModule = trainingModules[moduleId];
    if (foundModule) {
      console.log('✅ Module found:', foundModule.title);
      console.log('📅 Module validity:', foundModule.validityMinutes || 'default 6 months');
      setModule(foundModule);
      setLoading(false);
      loadUserData();
    } else {
      console.log('❌ Module not found for ID:', moduleId);
      setLoading(false);
    }
  }, [moduleId]);

  // Load user data including name from Firestore
  const loadUserData = async () => {
    if (!auth.currentUser || !module) return;

    console.log('=== loadUserData START ===');
    console.log('User ID:', auth.currentUser.uid);
    console.log('Auth email:', auth.currentUser.email);
    
    try {
      // Load user's data from Firestore
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // DEBUG: Log the raw data
        console.log('Firestore userData:', userData);
        console.log('Has "name" field?', 'name' in userData);
        console.log('Value of userData.name:', userData.name);
        
        // Get the name - SIMPLIFIED: only check userData.name
        let userName = '';
        
        if (userData.name && userData.name.trim() && userData.name.trim().length > 0) {
          userName = userData.name.trim();
          console.log('✅ Using name from Firestore:', userName);
        } else {
          // If name is empty string or falsy, use email
          userName = auth.currentUser.email.split('@')[0];
          console.log('⚠️ Name not found in Firestore, using email:', userName);
          
          // Try to fix the missing name in Firestore
          try {
            await updateDoc(doc(db, 'users', auth.currentUser.uid), {
              name: userName,
              updatedAt: new Date()
            });
            console.log('🔄 Updated Firestore with name from email');
          } catch (updateError) {
            console.log('Could not update Firestore:', updateError);
          }
        }
        
        console.log('Setting userName to:', userName);
        setUserName(userName);
        
      } else {
        console.log('❌ User document not found in Firestore');
        
        // Create the missing document
        try {
          const emailName = auth.currentUser.email.split('@')[0];
          console.log('🔄 Creating missing user document with name:', emailName);
          
          await setDoc(doc(db, 'users', auth.currentUser.uid), {
            name: emailName,
            email: auth.currentUser.email,
            role: auth.currentUser.displayName || 'user',
            createdAt: new Date(),
            status: 'active',
            lastLogin: new Date()
          });
          
          setUserName(emailName);
          console.log('✅ User document created');
          
        } catch (createError) {
          console.error('Failed to create user document:', createError);
          const emailName = auth.currentUser.email.split('@')[0];
          setUserName(emailName);
        }
      }
      
      // Load user progress
      await loadUserProgress();
      
    } catch (error) {
      console.error('❌ Error loading user data:', error);
      const emailName = auth.currentUser.email.split('@')[0];
      setUserName(emailName);
    }
    
    console.log('=== loadUserData END ===');
  };

  // Video engagement tracking functions
  const resetInactivityTimer = () => {
    setIsVideoActive(true);
    setUserInactiveTime(0);
    setShowInactiveWarning(false);
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      setShowInactiveWarning(true);
      warningTimerRef.current = setTimeout(() => {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
          setIsVideoActive(false);
          alert('Video paused due to inactivity. Please interact with the page to continue.');
        }
      }, 5000);
    }, 15000);
  };

  const handleUserActivity = () => {
    resetInactivityTimer();
  };

  // Set up event listeners for user activity
  useEffect(() => {
    if (currentStep === 'video' && module) {
      const events = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, handleUserActivity);
      });

      resetInactivityTimer();

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserActivity);
        });
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        if (warningTimerRef.current) {
          clearTimeout(warningTimerRef.current);
        }
      };
    }
  }, [currentStep, module]);

  const loadUserProgress = async () => {
    if (!auth.currentUser || !module) return;

    try {
      const progressRef = doc(db, 'userProgress', `${auth.currentUser.uid}_${moduleId}`);
      const progressDoc = await getDoc(progressRef);
      
      if (progressDoc.exists()) {
        const progress = progressDoc.data();
        console.log('📊 Existing progress:', progress);
        
        // Check if module validity has expired (6 months)
        const now = new Date();
        const validUntilDate = progress.validUntil?.toDate();
        const isExpired = validUntilDate && now > validUntilDate;
        
        if (isExpired && progress.isPassed) {
          // Reset progress if certification has expired
          console.log('🔄 Certification expired, resetting progress...');
          await updateDoc(progressRef, {
            status: 'expired',
            currentScore: 0,
            isPassed: false,
            validUntil: deleteField(),
            completedAt: deleteField()
          });
          
          // Reload with reset progress
          const updatedProgressDoc = await getDoc(progressRef);
          if (updatedProgressDoc.exists()) {
            const updatedProgress = updatedProgressDoc.data();
            setUserProgress(updatedProgress);
            setAttemptCount(updatedProgress.attemptCount || 0);
            setHasPassed(false);
            setQuizScore(0);
          }
        } else {
          // Progress is still valid
          setUserProgress(progress);
          setAttemptCount(progress.attemptCount || 0);
          
          if (validUntilDate && now < validUntilDate && progress.isPassed) {
            setHasPassed(true);
            setCurrentStep('result');
            setQuizScore(Math.round(progress.currentScore));
            setValidUntil(validUntilDate);
          }
        }
      } else {
        console.log('📝 No existing progress found');
      }
    } catch (error) {
      console.error('❌ Error loading progress:', error);
    }
  };

  const handleVideoEnd = () => {
    // Clear inactivity timers when video ends
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    // Set video as completed and show the popup
    setVideoCompleted(true);
    setShowQuizPopup(true);
  };

  const handleVideoPlay = () => {
    resetInactivityTimer();
  };

  const handleVideoPause = () => {
    resetInactivityTimer();
  };

  // New function to handle "Take Quiz" button click
  const handleTakeQuizClick = () => {
    setShowQuizPopup(false);
    setCurrentStep('quiz');
  };

  // New function to handle "Watch Again" button click
  const handleWatchAgainClick = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setShowQuizPopup(false);
      setVideoCompleted(false);
      resetInactivityTimer();
    }
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const calculateScore = () => {
    if (!module) return 0;
    
    let correct = 0;
    module.quiz.forEach(question => {
      if (quizAnswers[question.id] === question.correctAnswer) {
        correct++;
      }
    });
    const score = (correct / module.quiz.length) * 100;
    const roundedScore = Math.round(score);
    setQuizScore(roundedScore);
    return roundedScore;
  };

  const handleQuizSubmit = async () => {
    if (!module) return;
    
    console.log('=== handleQuizSubmit START ===');
    
    const score = calculateScore();
    const passed = score >= 80;

    if (!auth.currentUser) return;

    try {
      // FIRST: Get FRESH user data from Firestore to ensure we have the latest name
      let finalUserName = '';
      let userRole = auth.currentUser.displayName || '';
      
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const freshUserData = userDoc.data();
          
          // Get name from fresh data - SIMPLIFIED
          if (freshUserData.name && freshUserData.name.trim()) {
            finalUserName = freshUserData.name.trim();
            console.log('✅ Got name from fresh Firestore data:', finalUserName);
          } else {
            finalUserName = auth.currentUser.email.split('@')[0];
            console.log('⚠️ Using email as name:', finalUserName);
          }
          
          // Also get role if available
          if (freshUserData.role) {
            userRole = freshUserData.role;
          }
        } else {
          finalUserName = auth.currentUser.email.split('@')[0];
          console.log('❌ No user document, using email:', finalUserName);
        }
      } catch (fetchError) {
        console.error('Error fetching fresh user data:', fetchError);
        finalUserName = auth.currentUser.email.split('@')[0];
      }
      
      console.log('Final name to save:', finalUserName);
      console.log('Role to save:', userRole);
      
      // Save to userProgress
      const progressRef = doc(db, 'userProgress', `${auth.currentUser.uid}_${moduleId}`);
      
      // Set validity to 6 months from now
      const validUntilDate = calculateValidityDate();
      setValidUntil(validUntilDate);

      const newAttemptCount = attemptCount + 1;

      const progressData = {
        userId: auth.currentUser.uid,
        moduleId: moduleId,
        moduleTitle: module.title,
        moduleDepartment: module.department,
        userName: finalUserName,
        userEmail: auth.currentUser.email,
        userRole: userRole,
        status: passed ? 'completed' : 'in-progress',
        currentScore: score,
        isPassed: passed,
        lastAttempt: new Date(),
        attemptCount: newAttemptCount
      };

      // Add conditional fields
      if (passed) {
        progressData.validUntil = validUntilDate;
        progressData.completedAt = new Date();
        progressData.passedDate = new Date();
        progressData.status = 'completed';
        console.log('✅ Setting 6-month validity until:', validUntilDate.toLocaleDateString());
      } else {
        progressData.failedDate = new Date();
        progressData.status = 'in-progress';
      }

      console.log('Saving progress data:', progressData);
      await setDoc(progressRef, progressData, { merge: true });

      console.log('✅ Progress saved successfully!');
      
      // Update user's last training attempt
      try {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          lastTrainingAttempt: new Date(),
          lastModuleAttempted: moduleId,
          updatedAt: new Date()
        });
      } catch (userError) {
        console.log('Could not update user document:', userError);
      }
      
      setAttemptCount(newAttemptCount);
      setHasPassed(passed);
      setCurrentStep('result');
      
    } catch (error) {
      console.error('❌ Error saving progress:', error);
      alert('Error saving quiz results. Please try again.');
    }
    
    console.log('=== handleQuizSubmit END ===');
  };

  const handleRetryQuiz = () => {
    setQuizAnswers({});
    setQuizScore(0);
    
    // Reset video to beginning
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    
    setCurrentStep('video');
    setShowQuizPopup(false);
    setVideoCompleted(false);
    resetInactivityTimer();
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="training-container">
        <div className="training-card">
          <div className="loading-spinner">Loading module...</div>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="training-container">
        <div className="training-card">
          <h2>Module Not Found</h2>
          <p>The requested training module does not exist.</p>
          <p>Module ID: <strong>{moduleId}</strong></p>
          <p>Available modules: {Object.keys(trainingModules).join(', ')}</p>
          <button onClick={handleBackToDashboard} className="back-btn">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="training-container">
      <div className="training-card">
        {/* Header */}
        <div className="training-header">
          <h1>{module.title}</h1>
          <p className="department">{module.department}</p>
          <div className="progress-indicator">
            <div className={`step ${currentStep === 'video' ? 'active' : ''}`}>1. Watch Video</div>
            <div className={`step ${currentStep === 'quiz' ? 'active' : ''}`}>2. Take Quiz</div>
            <div className={`step ${currentStep === 'result' ? 'active' : ''}`}>3. Results</div>
          </div>
          {attemptCount > 0 && (
            <div className="attempt-counter">
              Attempt: {attemptCount}
            </div>
          )}
        </div>

        {/* Video Step */}
        {currentStep === 'video' && (
          <div className="video-step">
            {/* Quiz Popup Overlay */}
            {showQuizPopup && (
              <div className="quiz-popup-overlay">
                <div className="quiz-popup">
                  <div className="popup-header">
                    <span className="popup-icon">🎯</span>
                    <h3>Video Complete!</h3>
                  </div>
                  <div className="popup-content">
                    <p>You've successfully watched the training video.</p>
                    <p>Are you ready to take the quiz and test your knowledge?</p>
                    
                    <div className="popup-stats">
                      <div className="stat-item">
                        <span className="stat-label">Questions:</span>
                        <span className="stat-value">{module.quiz.length}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Passing Score:</span>
                        <span className="stat-value">80%</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Certification:</span>
                        <span className="stat-value">Valid for 6 months</span>
                      </div>
                      {attemptCount > 0 && (
                        <div className="stat-item">
                          <span className="stat-label">Previous Attempt:</span>
                          <span className="stat-value">Attempt #{attemptCount}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="popup-actions">
                      <button 
                        onClick={handleWatchAgainClick} 
                        className="popup-btn secondary"
                      >
                        <span className="btn-icon">↻</span>
                        Watch Again
                      </button>
                      <button 
                        onClick={handleTakeQuizClick} 
                        className="popup-btn primary"
                      >
                        <span className="btn-icon">📝</span>
                        Take Quiz Now
                      </button>
                    </div>
                    
                    <div className="popup-tip">
                      <span className="tip-icon">💡</span>
                      <p>Tip: The quiz questions are based directly on the video content you just watched.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className={`video-container ${showQuizPopup ? 'blurred' : ''}`}>
              {showInactiveWarning && (
                <div className="inactivity-warning">
                  <div className="warning-content">
                    <span className="warning-icon">⚠️</span>
                    <p>Are you still watching? Video will pause in 5 seconds...</p>
                  </div>
                </div>
              )}
              
              <video
                ref={videoRef}
                controls
                onEnded={handleVideoEnd}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                className="training-video"
                controlsList="nodownload"
                disablePictureInPicture
              >
                <source src={module.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              <div className="engagement-indicator">
                <div className={`status-dot ${isVideoActive ? 'active' : 'inactive'}`}></div>
                <span>{isVideoActive ? 'Active' : 'Inactive - Video Paused'}</span>
              </div>
            </div>
            
            <div className="video-instructions">
              <h3>Instructions</h3>
              <p>• Watch the entire video carefully</p>
              <p>• The video must complete before you can take the quiz</p>
              <p>• You cannot skip ahead or fast-forward</p>
              <p>• Stay active - video will pause if no interaction for 15 seconds</p>
              <p>• Pay attention as the quiz questions are based on video content</p>
              
              {attemptCount > 0 && (
                <div className="retake-notice">
                  <h4>🔄 Quiz Retake</h4>
                  <p>This is attempt #{attemptCount + 1}</p>
                  <p>Please rewatch the video carefully before retaking the quiz</p>
                </div>
              )}
              
              <div className="testing-notice">
                <h4>ℹ️ Certification Information</h4>
                <p>Certification validity: <strong>6 months</strong></p>
                <p>Passing score: <strong>80%</strong></p>
                <p>Inactivity timeout: 15 seconds</p>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Step */}
        {currentStep === 'quiz' && (
          <div className="quiz-step">
            <h3>Quiz: {module.title}</h3>
            <p className="quiz-instruction">Answer all questions. You need 80% to pass.</p>
            {attemptCount > 0 && (
              <p className="attempt-notice">Attempt: {attemptCount + 1}</p>
            )}
            
            <div className="questions-container">
              {module.quiz.map((question, index) => (
                <div key={question.id} className="question-card">
                  <h4>Question {index + 1}: {question.question}</h4>
                  <div className="options-grid">
                    {question.options.map((option, optionIndex) => (
                      <label key={optionIndex} className="option-label">
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={optionIndex}
                          checked={quizAnswers[question.id] === optionIndex}
                          onChange={() => handleAnswerSelect(question.id, optionIndex)}
                        />
                        <span className="option-text">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button 
              onClick={handleQuizSubmit}
              disabled={Object.keys(quizAnswers).length !== module.quiz.length}
              className="submit-quiz-btn"
            >
              Submit Quiz
            </button>
          </div>
        )}

        {/* Results Step */}
        {currentStep === 'result' && (
          <div className="result-step">
            <div className={`result-header ${hasPassed ? 'passed' : 'failed'}`}>
              <h2>{hasPassed ? '🎉 Congratulations!' : '📝 Needs Improvement'}</h2>
              <div className="score-circle">
                <span className="score">{quizScore}%</span>
              </div>
              <p className="result-message">
                {hasPassed 
                  ? `You have successfully passed the ${module.title} module!`
                  : `You scored ${quizScore}%. You need 80% to pass.`
                }
              </p>
              {!hasPassed && (
                <p className="attempt-info">This was attempt #{attemptCount}</p>
              )}
            </div>

            {!hasPassed && (
              <div className="failed-actions">
                <div className="correct-answers-section">
                  <h4>Correct Answers:</h4>
                  {module.quiz.map((question, index) => (
                    <div key={question.id} className="correct-answer">
                      <p><strong>Q{index + 1}:</strong> {question.question}</p>
                      <p><strong>Correct Answer:</strong> {question.options[question.correctAnswer]}</p>
                    </div>
                  ))}
                </div>
                
                <div className="retry-instructions">
                  <h4>Next Steps:</h4>
                  <p>• Please rewatch the video carefully</p>
                  <p>• Pay attention to the key points mentioned</p>
                  <p>• The quiz questions are based directly on the video content</p>
                </div>

                <button onClick={handleRetryQuiz} className="retry-btn">
                  ↻ Rewatch Video & Retry Quiz
                </button>
              </div>
            )}

            {hasPassed && (
              <div className="passed-actions">
                <div className="validity-info">
                  <p>✅ This certification is valid for <strong>6 months</strong></p>
                  <p>📅 Expires: {validUntil?.toLocaleDateString()}</p>
                  <p>🎯 Passed on attempt #{attemptCount} with {quizScore}%</p>
                </div>
                <button onClick={handleBackToDashboard} className="dashboard-btn">
                  Back to Dashboard
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingModule;